import React, { useState, useMemo, useEffect } from 'react';
import PageShell from '@/components/Layout/PageShell';
import { useRegisterUIContext } from '@/hooks';
import { useSearchParams } from 'react-router-dom';
import { getErrorDetail } from '@/utils/apiError';
import { cn } from '@/utils/cn';
import { PageHeader } from '@/components/UI/PageHeader';
import { SearchInput } from '@/components/UI/SearchInput';
import { Tabs } from '@/components/UI/Tabs';
import { Badge, type BadgeProps } from '@/components/UI/Badge';
import { Button } from '@/components/UI/Button';
import { IconButton } from '@/components/UI/IconButton';
import { EmptyState } from '@/components/UI/EmptyState';
import { LoadingState } from '@/components/UI/LoadingState';
import { fieldClass } from '@/components/UI/fieldStyles';
import {
  useListCatalogueEntriesQuery,
  useUpdateCatalogueConfigMutation,
  useDeleteCatalogueConfigMutation,
  useListPlatformTenantsQuery,
  useListPlatformRolesQuery,
  useListPlatformUsersQuery,
  CatalogueEntry,
  LayerOverride,
} from '@/redux/internal-services/platformAdminApi';
import { useListAgentsQuery } from '@/redux/internal-services/agentsApi';

// ── helpers ──────────────────────────────────────────────────────────────────

type Layer = 'system' | 'tenant' | 'role' | 'user';

// Setting types the API stores as structured JSON (api-service endpoints/configurations.py
// _STRUCTURAL_TYPES). They must be sent parsed — a quoted JSON string is rejected with a 400.
const isObjectType = (type: string) => type === 'dict' || type === 'object';
const isListType = (type: string) => type === 'list' || type === 'array';

function parseValue(type: string, raw: string): unknown {
  if (type === 'bool') return raw === 'true';
  if (type === 'int') {
    const n = parseInt(raw, 10);
    if (Number.isNaN(n)) throw new Error('Expected a whole number — e.g. 20');
    return n;
  }
  if (isObjectType(type)) {
    let parsed: unknown;
    try { parsed = JSON.parse(raw); } catch { throw new Error('Not valid JSON — e.g. {"key": true}'); }
    if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
      throw new Error('Expected a JSON object — e.g. {"key": true}');
    }
    return parsed;
  }
  if (isListType(type)) {
    // JSON.parse succeeds for ANY JSON scalar, so a bare `123` used to come back as the number 123
    // and `true` as a boolean — both sent straight to an endpoint that requires a list, which
    // answered "must be a JSON list, got int". Only a parsed ARRAY is taken as already-JSON;
    // anything else is treated as the comma-separated list the field is really asking for.
    const trimmed = raw.trim();
    if (!trimmed) return [];
    try {
      const parsed: unknown = JSON.parse(trimmed);
      if (Array.isArray(parsed)) return parsed;
    } catch {
      // not JSON at all — fall through to the comma list
    }
    return trimmed.split(',').map((s) => s.trim()).filter(Boolean);
  }
  return raw;
}

function displayVal(v: unknown): string {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'boolean') return v ? 'true' : 'false';
  if (Array.isArray(v)) return v.map((item) => (typeof item === 'object' && item !== null ? JSON.stringify(item) : String(item))).join(', ');
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

function displayPickerVal(v: unknown, options: PickerOption[]): string {
  const ids = Array.isArray(v) ? (v as unknown[]).map(String) : typeof v === 'string' && v ? v.split(',').map((s) => s.trim()).filter(Boolean) : [];
  if (ids.length === 0 || ids.includes('*')) return 'All agents';
  const names = ids.map((id) => options.find((o) => o.value === id)?.label ?? id);
  return names.join(', ');
}

// When an onReset sends a catalogue default (e.g. the bare string "*" for list settings),
// the backend _check_structural_types gate rejects it as the wrong structural type. Coerce the
// value through parseValue so "* -> ["*"] and the backend accepts it as a proper list.
// DB-stored values (systemVal / tenantVal) are already typed; only raw defaults need this.
function coerceForSave(type: string, v: unknown): unknown {
  if (isListType(type) && typeof v === 'string') return parseValue(type, v);
  return v;
}

function collectOverrides(overrides: LayerOverride[], settingKey: string) {
  return overrides
    .filter((o) => o.value[settingKey] !== undefined)
    .map((o) => ({ id: o.role_id ?? o.user_id ?? 'unknown', value: o.value[settingKey] }));
}

// ── override pill ─────────────────────────────────────────────────────────────

const PILL: Record<string, BadgeProps['variant']> = {
  orange: 'warning',
  purple: 'accent',
  pink:   'steel',
};
const DOT: Record<string, string> = {
  orange: 'bg-warning',
  purple: 'bg-accent',
  pink:   'bg-steel',
};

const OverridePill: React.FC<{ value: unknown; label?: string; color: string }> = ({ value, label, color }) => (
  <Badge
    variant={PILL[color]}
    className="max-w-[140px] gap-1.5 truncate font-mono"
    title={label ? `${label}: ${displayVal(value)}` : displayVal(value)}
  >
    <span className={cn('h-1.5 w-1.5 shrink-0 rounded-full', DOT[color])} />
    <span className="truncate">{displayVal(value)}</span>
  </Badge>
);

// A list setting whose members are ids of real records reads as an opaque comma-separated string
// in a text box. `options` turns it into a checkbox picker instead, with an explicit row for the
// "*" wildcard — the shape the backend treats as "no restriction".
export interface PickerOption { value: string; label: string; hint?: string }
const ALL_WILDCARD = '*';

const ListPicker: React.FC<{
  options: PickerOption[];
  selected: string[];
  onChange: (next: string[]) => void;
}> = ({ options, selected, onChange }) => {
  const [query, setQuery] = React.useState('');
  const all = selected.includes(ALL_WILDCARD);
  const toggle = (value: string) => {
    // When "All agents" is currently active, clicking a specific agent switches from
    // "unrestricted" to "only this agent" — clears the wildcard and starts a new selection.
    if (selected.includes(ALL_WILDCARD)) { onChange([value]); return; }
    onChange(selected.includes(value) ? selected.filter((v) => v !== value) : [...selected, value]);
  };
  const filtered = query.trim()
    ? options.filter((o) => o.label.toLowerCase().includes(query.toLowerCase()) || o.value.toLowerCase().includes(query.toLowerCase()))
    : options;
  return (
    <div className="flex w-72 max-w-full flex-col rounded-control border border-ui-border bg-ui-input">
      <div className="p-2 pb-1">
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search agents…"
          className="w-full rounded-chip border border-ui-border bg-ui-card px-2 py-1 text-xs text-ui-text placeholder:text-ui-text3 focus:outline-none focus:ring-1 focus:ring-ui-ring"
        />
      </div>
      <div className="flex max-h-52 flex-col gap-1 overflow-y-auto p-2 pt-1">
        {!query && (
          <>
            <label className="flex cursor-pointer items-center gap-2 rounded-chip px-1.5 py-1 hover:bg-ui-hover">
              <input type="checkbox" checked={all} onChange={() => onChange(all ? [] : [ALL_WILDCARD])} />
              <span className="text-xs font-medium text-ui-text">All agents</span>
              <span className="font-mono text-xs text-ui-text3">*</span>
            </label>
            <div className="my-0.5 h-px bg-ui-border" />
          </>
        )}
        {filtered.map((o) => (
          <label
            key={o.value}
            className={cn(
              'flex cursor-pointer items-center gap-2 rounded-chip px-1.5 py-1 hover:bg-ui-hover',
              all && !query && 'opacity-50',
            )}
          >
            <input type="checkbox" checked={!all && selected.includes(o.value)} onChange={() => toggle(o.value)} />
            <span className="min-w-0 flex-1 truncate text-xs text-ui-text">{o.label}</span>
            {o.hint && <span className="shrink-0 font-mono text-xs text-ui-text3">{o.hint}</span>}
          </label>
        ))}
        {filtered.length === 0 && <p className="px-1.5 py-1 text-xs text-ui-text3">No agents match.</p>}
        {options.length === 0 && !query && <p className="px-1.5 py-1 text-xs text-ui-text3">No agents found.</p>}
      </div>
    </div>
  );
};


// ── inline editor cell ────────────────────────────────────────────────────────

const EditCell: React.FC<{
  value: unknown;
  metaType: string;
  allowedValues?: string[];
  /** When given, a list setting is edited as a checkbox picker instead of a raw text field. */
  options?: PickerOption[];
  isDefault: boolean;
  saving: boolean;
  onSave: (v: unknown) => void;
  onReset: () => void;
}> = ({ value, metaType, allowedValues, options, isDefault, saving, onSave, onReset }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(displayVal(value));
  // Picker state: used instead of `draft` when options are provided (list type rendered as checkboxes).
  const [pickerSelected, setPickerSelected] = useState<string[]>(() => {
    if (options && isListType(metaType)) {
      if (Array.isArray(value)) return (value as unknown[]).map(String);
      if (typeof value === 'string' && value) return value.split(',').map((s) => s.trim()).filter(Boolean);
    }
    return [];
  });

  const [error, setError] = useState<string | null>(null);

  const save = () => {
    try {
      if (options && isListType(metaType)) { onSave(pickerSelected); setEditing(false); return; }
      onSave(parseValue(metaType, draft));
      setError(null);
      setEditing(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Invalid value.');
    }
  };
  const cancel = () => { setError(null); setEditing(false); };

  const isEnum = metaType === 'enum' && allowedValues && allowedValues.length > 0;
  const isStructured = isObjectType(metaType) || isListType(metaType);

  if (editing) {
    return (
      <div className="flex flex-col gap-1">
      {options && isListType(metaType) ? (
        /* ── list setting with known options: render as a checkbox picker ── */
        <div className="flex flex-col gap-2">
          <ListPicker options={options} selected={pickerSelected} onChange={setPickerSelected} />
          <div className="flex items-center gap-1">
            <Button type="button" size="xs" onClick={save} disabled={saving}>{saving ? '…' : 'Save'}</Button>
            <IconButton label="Cancel" size="xs" onClick={cancel}>✕</IconButton>
          </div>
        </div>
      ) : (
      <div className="flex items-center gap-1.5">
        {metaType === 'bool' ? (
          <select
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            className={cn(fieldClass({ size: 'xs' }), 'w-20')}
          >
            <option value="true">true</option>
            <option value="false">false</option>
          </select>
        ) : isEnum ? (
          <select
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            className={cn(fieldClass({ size: 'xs' }), 'w-36')}
          >
            {allowedValues!.map((opt) => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        ) : (
          <input
            autoFocus
            type={metaType === 'int' ? 'number' : 'text'}
            value={draft}
            onChange={(e) => { setDraft(e.target.value); setError(null); }}
            className={cn(fieldClass({ size: 'xs' }), isStructured ? 'w-72 max-w-full' : 'w-32')}
          />
        )}
        <Button type="button" size="xs" onClick={save} disabled={saving}>
          {saving ? '…' : 'Save'}
        </Button>
        <IconButton label="Cancel" size="xs" onClick={cancel}>✕</IconButton>
      </div>
      )} {/* end options ? picker : text/select */}
      {error && <p className="text-caption text-danger-text">{error}</p>}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <span className={cn('font-mono text-xs', isDefault ? 'text-ui-text3' : 'font-medium text-ui-text')}>
        {options && isListType(metaType) ? displayPickerVal(value, options) : displayVal(value)}
      </span>
      <div className="flex items-center gap-1">
        <IconButton
          label="Edit"
          size="xs"
          className="text-accent-text"
          onClick={() => { setDraft(displayVal(value)); setEditing(true); }}
        >
          <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
          </svg>
        </IconButton>
        {!isDefault && (
          <IconButton label="Reset to default" size="xs" className="text-warning-text" onClick={onReset}>
            <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </IconButton>
        )}
      </div>
    </div>
  );
};

// ── catalogue card ────────────────────────────────────────────────────────────

const CatalogueCard: React.FC<{
  entry: CatalogueEntry;
  activeLayer: Layer;
  selectedTenantId: string;
  selectedRoleId: string;
  selectedUserId: string;
  onSave: (catalogueKey: string, settingKey: string, value: unknown, targetLayer: Layer) => void;
  savingKey: string | null;
  onDelete: (catalogueKey: string) => void;
  deletingKey: string | null;
  autoExpand?: boolean;
  /** Setting keys → picker options (e.g. available_agents → list of Agent records). */
  optionsMap?: Record<string, PickerOption[]>;
}> = ({ entry, activeLayer, selectedTenantId, selectedRoleId, selectedUserId, onSave, savingKey, onDelete, deletingKey, autoExpand, optionsMap }) => {
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (autoExpand) setExpanded(true);
  }, [autoExpand]);

  // Catalogue-level editability for this layer
  const layerEditable = entry.layers?.[activeLayer]?.editable !== false; // default true if not declared

  // Filter settings to those visible for the active layer
  const allSettings = Object.entries(entry.settings ?? {});
  const settings = activeLayer === 'system'
    ? allSettings
    : allSettings.filter(([, sMeta]) => sMeta.layers?.[activeLayer]?.visible !== false);

  const tenantOverrideCount = Object.keys(entry.tenant_value ?? {}).length;
  const roleOverrideCount = entry.role_overrides?.length ?? 0;
  const userOverrideCount = entry.user_overrides?.length ?? 0;
  const anyOverride = tenantOverrideCount > 0 || roleOverrideCount > 0 || userOverrideCount > 0;

  // Which override column can be edited inline?
  const canEditTenant = layerEditable && activeLayer === 'tenant' && !!selectedTenantId;
  const canEditRole   = layerEditable && activeLayer === 'role'   && !!selectedRoleId;
  const canEditUser   = layerEditable && activeLayer === 'user'   && !!selectedUserId;

  // Whether the current focused scope has an override document to delete
  const hasOverrideToDelete =
    activeLayer !== 'system' && (
      (activeLayer === 'tenant' && selectedTenantId && Object.keys(entry.tenant_value ?? {}).length > 0) ||
      (activeLayer === 'role' && selectedRoleId && (entry.role_overrides ?? []).some((o) => o.role_id === selectedRoleId)) ||
      (activeLayer === 'user' && selectedUserId && (entry.user_overrides ?? []).some((o) => o.user_id === selectedUserId))
    );
  const isDeleting = deletingKey === entry.key;

  return (
    <div className="table-card">
      {/* header */}
      <button
        onClick={() => setExpanded((x) => !x)}
        className="flex w-full items-center justify-between px-5 py-3.5 text-left transition-colors hover:bg-ui-hover focus-ring"
      >
        <div className="flex items-center gap-3 min-w-0">
          <div className={cn('h-2 w-2 shrink-0 rounded-full', anyOverride ? 'bg-warning' : 'bg-success')} />
          <div className="min-w-0">
            <p className="truncate font-mono text-sm font-semibold text-ui-text">{entry.key}</p>
            {entry.description && (
              <p className="mt-0.5 max-w-lg truncate text-xs text-ui-text3">{entry.description}</p>
            )}
          </div>
        </div>
        <div className="ml-3 flex shrink-0 items-center gap-2">
          <div className="hidden items-center gap-2 sm:flex">
            {tenantOverrideCount > 0 && <Badge variant="warning">{tenantOverrideCount} tenant</Badge>}
            {roleOverrideCount > 0 && <Badge variant="accent">{roleOverrideCount} role</Badge>}
            {userOverrideCount > 0 && <Badge variant="steel">{userOverrideCount} user</Badge>}
            <span className="text-xs text-ui-text3">{settings.length} setting{settings.length !== 1 ? 's' : ''}</span>
          </div>
          {hasOverrideToDelete && (
            <IconButton
              label="Delete this layer's override (reverts to previous layer)"
              size="sm"
              onClick={(e) => { e.stopPropagation(); onDelete(entry.key); }}
              disabled={isDeleting}
              className="text-danger-text hover:bg-danger/10"
            >
              {isDeleting ? (
                <svg className="h-4 w-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              ) : (
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              )}
            </IconButton>
          )}
          <svg className={cn('h-4 w-4 shrink-0 text-ui-text3 transition-transform', expanded && 'rotate-180')} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>

      {/* table */}
      {expanded && settings.length > 0 && (
        <div className="no-scrollbar overflow-x-auto border-t border-ui-border">
          <table className="w-full min-w-[500px]">
            <thead>
              <tr>
                <th className="th-cell w-[28%]">Setting</th>
                <th className="th-cell w-10">Type</th>
                {activeLayer === 'system' && (
                  <>
                    <th className="th-cell w-[12%]">Default</th>
                    <th className="th-cell w-[24%]">
                      <span className="flex items-center gap-1"><span className="inline-block h-2 w-2 rounded-full bg-accent" />System value</span>
                    </th>
                  </>
                )}
                {activeLayer === 'tenant' && (
                  <>
                    <th className="th-cell w-[22%]">
                      <span className="flex items-center gap-1"><span className="inline-block h-2 w-2 rounded-full bg-warning" />Tenant override</span>
                    </th>
                    <th className="th-cell w-[22%]">
                      <span className="flex items-center gap-1"><span className="inline-block h-2 w-2 rounded-full bg-success" />Effective value</span>
                    </th>
                  </>
                )}
                {activeLayer === 'role' && (
                  <>
                    <th className="th-cell w-[22%]">
                      <span className="flex items-center gap-1"><span className="inline-block h-2 w-2 rounded-full bg-accent" />Role override</span>
                    </th>
                    <th className="th-cell w-[22%]">
                      <span className="flex items-center gap-1"><span className="inline-block h-2 w-2 rounded-full bg-success" />Effective value</span>
                    </th>
                  </>
                )}
                {activeLayer === 'user' && (
                  <>
                    <th className="th-cell w-[22%]">
                      <span className="flex items-center gap-1">
                        <span className="inline-block h-2 w-2 rounded-full bg-steel" />
                        User override
                        {!layerEditable && <span className="ml-1 text-ui-text3 italic normal-case">(read-only)</span>}
                      </span>
                    </th>
                    <th className="th-cell w-[22%]">
                      <span className="flex items-center gap-1"><span className="inline-block h-2 w-2 rounded-full bg-success" />Effective value</span>
                    </th>
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              {settings.map(([sKey, sMeta]) => {
                const systemVal   = (entry.system_value ?? {})[sKey];
                const tenantVal   = (entry.tenant_value ?? {})[sKey];
                const effectiveVal = (entry.effective_value ?? {})[sKey];
                const defaultVal  = sMeta.default;
                const systemIsDefault = systemVal === undefined || systemVal === defaultVal;
                const roleVals  = collectOverrides(entry.role_overrides ?? [], sKey);
                const userVals  = collectOverrides(entry.user_overrides ?? [], sKey);

                // The single override value for the focused role/user (when one is selected)
                const focusedRoleVal = selectedRoleId
                  ? (entry.role_overrides ?? []).find((o) => o.role_id === selectedRoleId)?.value?.[sKey]
                  : undefined;
                const focusedUserVal = selectedUserId
                  ? (entry.user_overrides ?? []).find((o) => o.user_id === selectedUserId)?.value?.[sKey]
                  : undefined;

                return (
                  <tr key={sKey} className="transition-colors hover:bg-ui-hover">
                    <td className="td-cell align-top">
                      <p className="text-xs font-medium font-mono text-ui-text">{sKey}</p>
                      {sMeta.description && <p className="text-xs text-ui-text3 mt-0.5 leading-snug">{sMeta.description}</p>}
                    </td>
                    <td className="td-cell align-top">
                      <Badge variant="neutral" className="font-mono">{sMeta.type}</Badge>
                    </td>

                    {/* System layer: default + system value (editable) */}
                    {activeLayer === 'system' && (
                      <>
                        <td className="td-cell align-top">
                          <span className="text-xs font-mono text-ui-text3">{displayVal(defaultVal)}</span>
                        </td>
                        <td className="td-cell align-top">
                          <EditCell
                            value={systemVal !== undefined ? systemVal : defaultVal}
                            metaType={sMeta.type}
                            isDefault={systemIsDefault}
                            saving={savingKey === `system.${entry.key}.${sKey}`}
                            onSave={(v) => onSave(entry.key, sKey, v, 'system')}
                            onReset={() => onSave(entry.key, sKey, coerceForSave(sMeta.type, defaultVal), 'system')}
                            allowedValues={sMeta.validation?.allowed}
                              options={optionsMap?.[sKey]}
                          />
                        </td>
                      </>
                    )}

                    {/* Tenant override + effective */}
                    {activeLayer === 'tenant' && (
                      <>
                        <td className="td-cell align-top">
                          {canEditTenant ? (
                            <EditCell
                              value={tenantVal !== undefined ? tenantVal : (systemVal !== undefined ? systemVal : defaultVal)}
                              metaType={sMeta.type}
                              isDefault={tenantVal === undefined || tenantVal === null}
                              saving={savingKey === `tenant.${entry.key}.${sKey}`}
                              onSave={(v) => onSave(entry.key, sKey, v, 'tenant')}
                              onReset={() => onSave(entry.key, sKey, coerceForSave(sMeta.type, systemVal !== undefined ? systemVal : defaultVal), 'tenant')}
                              allowedValues={sMeta.validation?.allowed}
                              options={optionsMap?.[sKey]}
                            />
                          ) : tenantVal !== undefined && tenantVal !== null ? (
                            <OverridePill value={tenantVal} color="orange" />
                          ) : (
                            <span className="text-xs text-ui-text3 italic">— inherits</span>
                          )}
                        </td>
                        <td className="td-cell align-top">
                          <span className="text-xs font-mono font-semibold text-ui-text">{optionsMap?.[sKey] ? displayPickerVal(effectiveVal !== undefined ? effectiveVal : defaultVal, optionsMap[sKey]!) : displayVal(effectiveVal !== undefined ? effectiveVal : defaultVal)}</span>
                        </td>
                      </>
                    )}

                    {/* Role override + effective */}
                    {activeLayer === 'role' && (
                      <>
                        <td className="td-cell align-top">
                          {canEditRole ? (
                            <EditCell
                              value={focusedRoleVal !== undefined ? focusedRoleVal : (tenantVal !== undefined ? tenantVal : (systemVal !== undefined ? systemVal : defaultVal))}
                              metaType={sMeta.type}
                              isDefault={focusedRoleVal === undefined || focusedRoleVal === null}
                              saving={savingKey === `role.${entry.key}.${sKey}`}
                              onSave={(v) => onSave(entry.key, sKey, v, 'role')}
                              onReset={() => onSave(entry.key, sKey, coerceForSave(sMeta.type, tenantVal !== undefined ? tenantVal : (systemVal !== undefined ? systemVal : defaultVal)), 'role')}
                              allowedValues={sMeta.validation?.allowed}
                              options={optionsMap?.[sKey]}
                            />
                          ) : roleVals.length > 0 ? (
                            <div className="flex flex-col gap-1">{roleVals.map((r) => <OverridePill key={r.id} value={r.value} label={r.id} color="purple" />)}</div>
                          ) : (
                            <span className="text-xs text-ui-text3 italic">— inherits</span>
                          )}
                        </td>
                        <td className="td-cell align-top">
                          <span className="text-xs font-mono font-semibold text-ui-text">{optionsMap?.[sKey] ? displayPickerVal(effectiveVal !== undefined ? effectiveVal : defaultVal, optionsMap[sKey]!) : displayVal(effectiveVal !== undefined ? effectiveVal : defaultVal)}</span>
                        </td>
                      </>
                    )}

                    {/* User override + effective */}
                    {activeLayer === 'user' && (
                      <>
                        <td className="td-cell align-top">
                          {canEditUser ? (
                            <EditCell
                              value={focusedUserVal !== undefined ? focusedUserVal : (tenantVal !== undefined ? tenantVal : (systemVal !== undefined ? systemVal : defaultVal))}
                              metaType={sMeta.type}
                              isDefault={focusedUserVal === undefined || focusedUserVal === null}
                              saving={savingKey === `user.${entry.key}.${sKey}`}
                              onSave={(v) => onSave(entry.key, sKey, v, 'user')}
                              onReset={() => onSave(entry.key, sKey, coerceForSave(sMeta.type, tenantVal !== undefined ? tenantVal : (systemVal !== undefined ? systemVal : defaultVal)), 'user')}
                              allowedValues={sMeta.validation?.allowed}
                              options={optionsMap?.[sKey]}
                            />
                          ) : userVals.length > 0 ? (
                            <div className="flex flex-col gap-1">{userVals.map((u) => <OverridePill key={u.id} value={u.value} label={u.id} color="pink" />)}</div>
                          ) : (
                            <span className="text-xs text-ui-text3 italic">— inherits</span>
                          )}
                        </td>
                        <td className="td-cell align-top">
                          <span className="text-xs font-mono font-semibold text-ui-text">{optionsMap?.[sKey] ? displayPickerVal(effectiveVal !== undefined ? effectiveVal : defaultVal, optionsMap[sKey]!) : displayVal(effectiveVal !== undefined ? effectiveVal : defaultVal)}</span>
                        </td>
                      </>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {expanded && settings.length === 0 && (
        <div className="border-t border-ui-border px-5 py-4"><EmptyState variant="inline" title="No settings defined." /></div>
      )}
    </div>
  );
};

// ── select field ─────────────────────────────────────────────────────────────

const FilterSelect: React.FC<{
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
  placeholder?: string;
  loading?: boolean;
}> = ({ label, value, onChange, options, placeholder = 'Select…', loading }) => (
  <div className="flex w-full flex-col gap-1 sm:w-auto sm:min-w-[180px]">
    <label className="eyebrow">{label}</label>
    <div className="relative">
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={loading}
        className={cn(fieldClass({ size: 'sm' }), 'appearance-none pr-8')}
      >
        <option value="">{loading ? 'Loading…' : placeholder}</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
      <svg className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ui-text3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
    </div>
  </div>
);

// ── page ──────────────────────────────────────────────────────────────────────

const ConfigurationsPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialPrefix = searchParams.get('prefix') ?? '';
  const [search, setSearch] = useState(initialPrefix);
  const [activeLayer, setActiveLayer] = useState<Layer>('system');

  // Remove the ?prefix param from the URL after applying it so Back/refresh behave normally
  useEffect(() => {
    if (searchParams.has('prefix')) {
      setSearchParams({}, { replace: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [selectedTenantId, setSelectedTenantId] = useState('');
  const [selectedRoleId, setSelectedRoleId] = useState('');
  const [selectedUserId, setSelectedUserId] = useState('');
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [deletingKey, setDeletingKey] = useState<string | null>(null);
  const [toast, setToast] = useState<{ type: 'error' | 'success'; text: string } | null>(null);

  // catalogue query — passes scope params based on active layer
  const catalogueParams = useMemo(() => {
    if (activeLayer === 'system') return {};
    if (activeLayer === 'tenant') return selectedTenantId ? { scopeTenantId: selectedTenantId } : undefined;
    if (activeLayer === 'role') return selectedTenantId ? { scopeTenantId: selectedTenantId, ...(selectedRoleId ? { scopeRoleId: selectedRoleId } : {}) } : undefined;
    if (activeLayer === 'user') return selectedTenantId ? { scopeTenantId: selectedTenantId, ...(selectedUserId ? { scopeUserId: selectedUserId } : {}) } : undefined;
    return {};
  }, [activeLayer, selectedTenantId, selectedRoleId, selectedUserId]);

  const skipCatalogue = (activeLayer !== 'system') && !selectedTenantId;

  // Agents list for the available_agents picker
  const { data: agentsData } = useListAgentsQuery({ status: 'active', limit: 200 });
  const agentPickerOptions: PickerOption[] = (agentsData?.data ?? []).map((a) => ({
    value: a.agent_id,
    label: a.agent_name,
  }));

  const { data, isLoading, refetch } = useListCatalogueEntriesQuery(
    catalogueParams ?? {},
    { skip: skipCatalogue, refetchOnMountOrArgChange: true }
  );
  const [updateConfig] = useUpdateCatalogueConfigMutation();
  const [deleteConfig] = useDeleteCatalogueConfigMutation();

  // tenant list for dropdown
  const { data: tenantsData, isLoading: tenantsLoading } = useListPlatformTenantsQuery(undefined, {
    skip: activeLayer === 'system',
  });

  // roles for selected tenant (role layer only)
  const { data: rolesData, isLoading: rolesLoading } = useListPlatformRolesQuery(
    { tenantId: selectedTenantId },
    { skip: activeLayer !== 'role' || !selectedTenantId }
  );

  // users for selected tenant (user layer only)
  const { data: usersData, isLoading: usersLoading } = useListPlatformUsersQuery(
    { tenant_id: selectedTenantId, limit: 200 },
    { skip: activeLayer !== 'user' || !selectedTenantId }
  );

  const entries: CatalogueEntry[] = data?.data ?? [];

  const tenantOptions = (tenantsData?.data ?? []).map((t) => ({
    value: t.tenant_id,
    label: t.company_name ?? t.tenant_id,
  }));

  const roleOptions = (rolesData?.data ?? []).map((r) => ({
    value: r.role_id,
    label: r.name ?? r.role_id,
  }));

  const userOptions = (usersData?.data ?? []).map((u) => ({
    value: u.user_id,
    label: u.name ? `${u.name} (${u.email ?? ''})` : (u.email ?? u.user_id),
  }));

  const filtered = useMemo(() => {
    if (!search.trim()) return entries;
    const q = search.toLowerCase();
    return entries.filter(
      (e) =>
        e.key.toLowerCase().includes(q) ||
        (e.description ?? '').toLowerCase().includes(q) ||
        Object.keys(e.settings ?? {}).some((s) => s.toLowerCase().includes(q))
    );
  }, [entries, search]);

  // Publish what this settings page is showing into the global UI-context store so SAM can answer
  // "what am I looking at?" — catalogue KEYS + active layer + scope only, never raw values
  // (permission scoping, SAM-FR-01-006).
  useRegisterUIContext(
    {
      mode: 'settings',
      surfaces: [{
        kind: 'settings',
        area: 'platform.configurations',
        activeLayer,
        visibleKeys: filtered.map((e) => e.key),
        scope: {
          tenantId: selectedTenantId || undefined,
          roleId: selectedRoleId || undefined,
          userId: selectedUserId || undefined,
        },
      }],
    },
    [activeLayer, filtered, selectedTenantId, selectedRoleId, selectedUserId],
  );

  const showToast = (type: 'error' | 'success', text: string) => {
    setToast({ type, text });
    setTimeout(() => setToast(null), 3000);
  };

  const handleLayerChange = (layer: Layer) => {
    setActiveLayer(layer);
    setSelectedTenantId('');
    setSelectedRoleId('');
    setSelectedUserId('');
  };

  const handleTenantChange = (id: string) => {
    setSelectedTenantId(id);
    setSelectedRoleId('');
    setSelectedUserId('');
  };

  const handleSave = async (catalogueKey: string, settingKey: string, value: unknown, targetLayer: Layer) => {
    const key = `${targetLayer}.${catalogueKey}.${settingKey}`;
    setSavingKey(key);

    try {
      const scopeTenantId = targetLayer !== 'system' ? selectedTenantId : undefined;
      const scopeRoleId   = targetLayer === 'role' ? selectedRoleId : undefined;
      const scopeUserId   = targetLayer === 'user' ? selectedUserId : undefined;
      await updateConfig({ key: catalogueKey, value: { [settingKey]: value }, scopeTenantId, scopeRoleId, scopeUserId }).unwrap();
      showToast('success', `Saved ${settingKey}`);
      refetch();
    } catch (err: unknown) {
      showToast('error', getErrorDetail(err) ?? 'Failed to save.');
    } finally {
      setSavingKey(null);
    }
  };

  const handleDelete = async (catalogueKey: string) => {
    if (!selectedTenantId) return;
    setDeletingKey(catalogueKey);
    try {
      const scopeRoleId = activeLayer === 'role' ? selectedRoleId || undefined : undefined;
      const scopeUserId = activeLayer === 'user' ? selectedUserId || undefined : undefined;
      await deleteConfig({ key: catalogueKey, scopeTenantId: selectedTenantId, scopeRoleId, scopeUserId }).unwrap();
      showToast('success', `Override removed — values now inherit from the layer below.`);
      refetch();
    } catch (err: unknown) {
      showToast('error', getErrorDetail(err) ?? 'Failed to delete override.');
    } finally {
      setDeletingKey(null);
    }
  };

  const LAYERS: { value: Layer; label: string; color: string }[] = [
    { value: 'system',  label: 'System',  color: 'blue'   },
    { value: 'tenant',  label: 'Tenant',  color: 'orange' },
    { value: 'role',    label: 'Role',    color: 'purple' },
    { value: 'user',    label: 'User',    color: 'pink'   },
  ];

  const DOT_CLASS: Record<string, string> = {
    blue:   'bg-accent',
    orange: 'bg-warning',
    purple: 'bg-accent',
    pink:   'bg-steel',
  };

  const needsTenantPick = activeLayer !== 'system' && !selectedTenantId;

  return (
    <PageShell
      maxWidth="max-w-6xl"
      header={
        <PageHeader
          title="Platform Configurations"
          subtitle="View and manage configuration overrides across all layers."
          toolbar={
            <div className="space-y-3">
              {/* layer tabs */}
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                <span className="eyebrow shrink-0">Layer</span>
                <Tabs
                  variant="segmented"
                  label="Configuration layer"
                  tabs={LAYERS.map((l) => ({
                    id: l.value,
                    label: (
                      <span className="flex items-center gap-1.5">
                        <span className={cn('h-2 w-2 rounded-full', DOT_CLASS[l.color])} />
                        {l.label}
                      </span>
                    ),
                  }))}
                  value={activeLayer}
                  onChange={(id) => handleLayerChange(id as Layer)}
                />
              </div>

              {/* cascading sub-filters */}
              {activeLayer !== 'system' && (
                <div className="flex flex-wrap items-end gap-4">
                  <FilterSelect
                    label="Tenant"
                    value={selectedTenantId}
                    onChange={handleTenantChange}
                    options={tenantOptions}
                    placeholder="Select tenant…"
                    loading={tenantsLoading}
                  />
                  {activeLayer === 'role' && selectedTenantId && (
                    <FilterSelect
                      label="Role (optional)"
                      value={selectedRoleId}
                      onChange={setSelectedRoleId}
                      options={roleOptions}
                      placeholder="All roles"
                      loading={rolesLoading}
                    />
                  )}
                  {activeLayer === 'user' && selectedTenantId && (
                    <FilterSelect
                      label="User (optional)"
                      value={selectedUserId}
                      onChange={setSelectedUserId}
                      options={userOptions}
                      placeholder="All users"
                      loading={usersLoading}
                    />
                  )}
                  {selectedTenantId && (
                    <Button
                      type="button"
                      variant="link"
                      size="xs"
                      className="mb-2"
                      onClick={() => { setSelectedTenantId(''); setSelectedRoleId(''); setSelectedUserId(''); }}
                    >
                      Clear
                    </Button>
                  )}
                </div>
              )}

              {/* search + result count */}
              <div className="flex items-center gap-3">
                <div className="relative max-w-sm flex-1">
                  <SearchInput
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search keys, settings, descriptions…"
                    className="pr-10 [&::-webkit-search-cancel-button]:appearance-none"
                  />
                  {search && (
                    <IconButton
                      label="Clear search"
                      size="xs"
                      className="absolute right-3 top-1/2 -translate-y-1/2"
                      onClick={() => setSearch('')}
                    >
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                    </IconButton>
                  )}
                </div>
                {!isLoading && !needsTenantPick && (
                  <span className="text-xs text-ui-text3">{filtered.length} of {entries.length} catalogues</span>
                )}
              </div>
            </div>
          }
        />
      }
    >

      {/* toast */}
      {toast && (
        <div className={cn(
          'fixed bottom-6 right-6 z-toast flex items-center gap-2 rounded-card px-4 py-3 text-sm font-medium shadow-pop',
          toast.type === 'success' ? 'border border-success/30 bg-success/10 text-success-text' : 'border border-danger/30 bg-danger/10 text-danger-text',
        )}>
          {toast.type === 'success'
            ? <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
            : <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>}
          {toast.text}
        </div>
      )}

      {/* content */}
      {needsTenantPick ? (
        <EmptyState
          className="rounded-card border border-dashed border-ui-border py-16"
          icon={
            <svg className="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
          }
          title={`Select a tenant to view ${activeLayer} overrides`}
          description={
            activeLayer === 'role' ? 'Select a tenant, then optionally a role to enable inline editing.' :
            activeLayer === 'user' ? 'Select a tenant, then optionally a user to enable inline editing.' :
            'Use the Tenant dropdown above to scope the view.'
          }
        />
      ) : isLoading ? (
        <LoadingState label="Loading configurations…" className="py-20" />
      ) : filtered.length === 0 ? (
        <EmptyState
          className="rounded-card border border-dashed border-ui-border py-16"
          title="No configurations match your filters."
          action={search ? (
            <Button type="button" variant="link" size="xs" className="text-accent-text" onClick={() => setSearch('')}>Clear search</Button>
          ) : undefined}
        />
      ) : (
        <div className="space-y-3">
          {filtered.map((entry) => (
            <CatalogueCard
              key={entry.key}
              entry={entry}
              activeLayer={activeLayer}
              selectedTenantId={selectedTenantId}
              selectedRoleId={selectedRoleId}
              selectedUserId={selectedUserId}
              onSave={handleSave}
              savingKey={savingKey}
              onDelete={handleDelete}
              deletingKey={deletingKey}
              autoExpand={!!initialPrefix && entry.key === initialPrefix}
              optionsMap={entry.key === 'available_agents' ? { available_agents: agentPickerOptions } : undefined}
            />
          ))}
        </div>
      )}
    </PageShell>
  );
};

export default ConfigurationsPage;
